import { HttpClient } from "@angular/common/http";
import { SoLoginDialog } from "./soLoginDialog";
import { Inject, Injectable } from "@angular/core";
import { ConfigurationService } from "../service/configuration.service";
import { OutlookService } from "../service/outlook/outlook.service";

/**
 * class used to initiate authentication OpenID connect request to
 * SuperOffice Online and intercept callback.
 *
 * The rest of the OIDC flow (code-exchange, renewal, etc) is handled C#-side.
 */
@Injectable({
  providedIn: "root",
})
export class SoAuthenticator {

  config: IAuthenticatorConfiguration;

  constructor(
    private readonly configService: ConfigurationService,
    private readonly httpClient: HttpClient,
    private readonly loginDialog: SoLoginDialog,
    @Inject("BASE_URL") private readonly baseUrl: string,
  ) {
    this.config = {
      clientId: this.configService.config.clientId,
      baseUrl: this.configService.config.authBaseUrl,
      authorizeUrl: this.configService.config.authorizeEndpoint,
      redirectUrl: `${this.baseUrl}${this.configService.config.redirectEndpoint}`,
      responseType: "code",
      scope: "openid",
      tokenUrl: `${this.baseUrl}${this.configService.config.serverTokenEndpoint}`,
      extraQueryParameters: { nonce: Math.random().toString() }
    }
  }

  /**
   * Check if the current url is running inside of a Dialog that contains an access_token, code, or error.
   * If true then it calls messageParent by extracting the token information, thereby closing the dialog.
   * Otherwise, the caller should proceed with normal initialization of their application.
   *
   * This logic assumes that the redirect url is your application and hence when your code runs again in
   * the dialog, this logic takes over and closes it for you.
   *
   * Copied and adapter from:
   * https://github.com/OfficeDev/office-js-helpers/blob/master/src/authentication/authenticator.ts#L73-L94.
   */
  static isAuthDialog(): boolean {
    // If the url doesn't contain an access_token, code, or error then return false.
    // This is in scenarios where we don't want to automatically control what happens to the dialog.
    if (!/(access_token|code|error|state)/gi.test(location.href)) {
      return false;
    }

    new SoLoginDialog(new OutlookService()).close(location.href);
    return true;
  }

  async authenticate(): Promise<void> {
    const loginUrl = this.getLoginUrl();
    // callback url from online login, including code for code-exchange!
    const callbackUrl = await this.getCallbackUrl(loginUrl);
    const code = this.getCodeFromUrl(callbackUrl);

    // this posts code to backend, which stores resulting access & refresh tokens
    await this.exchangeCode(code);

    // login complete! results retrieved from /api/auth/isLoggedIn!
  }

  private getLoginUrl(): string {
    const scope = (this.config.scope) ? encodeURIComponent(this.config.scope) : null;

    const urlSegments = [
      `response_type=${this.config.responseType}`,
      `client_id=${encodeURIComponent(this.config.clientId)}`,
      `redirect_uri=${encodeURIComponent(this.config.redirectUrl)}`
    ];

    if (scope) {
      urlSegments.push(`scope=${scope}`);
    }
    if (this.config.extraQueryParameters) {
      for (const param of Object.keys(this.config.extraQueryParameters)) {
        urlSegments.push(`${param}=${encodeURIComponent(this.config.extraQueryParameters[param])}`);
      }
    }

    return `${this.config.baseUrl}${this.config.authorizeUrl}?${urlSegments.join("&")}`;
  }

  private async getCallbackUrl(loginUrl: string): Promise<string> {
    const url = await this.loginDialog.getUrlWithCode(loginUrl);
    // loginWindow is closed in SoAuthenticator.isAuthDialog(), called from main.ts!
    return url;
  }

  private getCodeFromUrl(url: string): string {
    const pos = url.indexOf("code=");
    if (pos === -1) {
      throw new Error("Code not found in url!");
    } else {
      return url.substring(pos + 5);
    }
  }

  private async exchangeCode(code: string): Promise<void> {
    // use httpclient to exchange code, since we depend on ClientApp bearer to
    // identify the current user in the backend, and this gets processed by interceptors.
    await this.httpClient.post(this.config.tokenUrl, { code }).toPromise();
  }
}

export interface IAuthenticatorConfiguration {
  // Registered OAuth ClientID
  clientId: string;

  // Base URL of the endpoint
  baseUrl: string;

  // URL segment for OAuth authorize endpoint.
  // The final authorize url is constructed as (baseUrl + '/' + authorizeUrl).
  authorizeUrl: string;

  // Registered OAuth redirect url.
  // Defaults to window.location.origin
  redirectUrl: string;

  // Optional token url to exchange a code with.
  // Not recommended if OAuth provider supports implicit flow.
  tokenUrl: string;

  // Registered OAuth scope.
  scope: string;

  // Automatically generate a state? defaults to false.
  state?: boolean;

  // OAuth responseType.
  responseType?: string;

  // Additional object for query parameters.
  // Will be appending them after encoding the values.
  extraQueryParameters?: { [index: string]: string };
}
